/**
 * Simple Session-based Authentication API
 * Replaces JWT complexity with straightforward session cookies
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface User {
  id: number;
  email: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  emailVerified: boolean;
  isAlphaUser: boolean;
  registrationSource: string;
  createdAt: string;
  lastLogin?: string;
  lastActivity?: string;
}

interface LoginResponse {
  success: boolean;
  message: string;
  user: User;
  sessionToken: string; // Also provided in response for debugging
  rememberMe: boolean;
}

interface RegisterResponse {
  success: boolean;
  message: string;
  user: {
    id: number;
    email: string;
    registration_source: string;
  };
  email_verification_required: boolean;
}

interface AuthCheckResponse {
  success: boolean;
  authenticated: boolean;
  user: User | null;
}

interface ErrorResponse {
  success: false;
  error: string;
  details?: string[];
  message?: string;
}

class SimpleAuthAPI {
  
  private async makeRequest(
    url: string, 
    options: RequestInit = {}
  ): Promise<any> {
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // CRITICAL: Always include credentials to send/receive session cookies
    const requestOptions: RequestInit = {
      ...options,
      headers,
      credentials: 'include', // This sends session cookies automatically
    };

    // Add timeout protection
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000); // 15 second timeout
    requestOptions.signal = controller.signal;

    try {
      console.log(`[SimpleAuthAPI] Making ${options.method || 'GET'} request to: ${url}`);
      
      const response = await fetch(`${API_BASE_URL}${url}`, requestOptions);
      clearTimeout(timeout);

      // Handle response
      const contentType = response.headers.get('content-type');
      let data;

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 200));
        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
      }

      // Log response for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log(`[SimpleAuthAPI] Response:`, { status: response.status, data });
      }

      if (!response.ok) {
        throw new Error(data?.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return data;
    } catch (error: any) {
      clearTimeout(timeout);
      
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please try again');
      }
      
      console.error(`[SimpleAuthAPI] Request failed:`, error);
      throw error;
    }
  }

  async login(email: string, password: string, rememberMe: boolean = false): Promise<LoginResponse> {
    try {
      console.log('[SimpleAuthAPI] Attempting login for:', email);
      
      const response = await this.makeRequest('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          email,
          password,
          remember_me: rememberMe
        }),
      });

      console.log('[SimpleAuthAPI] Login successful');
      return response;
    } catch (error: any) {
      console.error('[SimpleAuthAPI] Login failed:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      console.log('[SimpleAuthAPI] Attempting logout');
      
      await this.makeRequest('/api/auth/logout', {
        method: 'POST'
      });

      console.log('[SimpleAuthAPI] Logout successful');
    } catch (error: any) {
      console.error('[SimpleAuthAPI] Logout failed:', error);
      // Don't throw - logout should always succeed from frontend perspective
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      console.log('[SimpleAuthAPI] Fetching current user info');
      
      const response = await this.makeRequest('/api/auth/me', {
        method: 'GET'
      });

      if (response.success && response.user) {
        console.log('[SimpleAuthAPI] Current user fetched successfully');
        return response.user;
      } else {
        throw new Error('Invalid user response');
      }
    } catch (error: any) {
      console.error('[SimpleAuthAPI] Failed to get current user:', error);
      throw error;
    }
  }

  async checkAuthentication(): Promise<AuthCheckResponse> {
    try {
      console.log('[SimpleAuthAPI] Checking authentication status');
      
      const response = await this.makeRequest('/api/auth/check', {
        method: 'GET'
      });

      console.log('[SimpleAuthAPI] Authentication check result:', response.authenticated);
      return response;
    } catch (error: any) {
      console.error('[SimpleAuthAPI] Authentication check failed:', error);
      return {
        success: false,
        authenticated: false,
        user: null
      };
    }
  }

  async register(
    email: string, 
    password: string, 
    firstName?: string, 
    lastName?: string,
    invitationCode?: string
  ): Promise<RegisterResponse> {
    try {
      console.log('[SimpleAuthAPI] Attempting registration for:', email);
      
      const response = await this.makeRequest('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          email,
          password,
          first_name: firstName,
          last_name: lastName,
          invitation_code: invitationCode
        }),
      });

      console.log('[SimpleAuthAPI] Registration successful');
      return response;
    } catch (error: any) {
      console.error('[SimpleAuthAPI] Registration failed:', error);
      throw error;
    }
  }

  async verifyEmail(token: string): Promise<boolean> {
    try {
      console.log('[SimpleAuthAPI] Verifying email with token');
      
      const response = await this.makeRequest('/api/auth/verify-email', {
        method: 'POST',
        body: JSON.stringify({
          verification_token: token
        }),
      });

      return response.success;
    } catch (error: any) {
      console.error('[SimpleAuthAPI] Email verification failed:', error);
      return false;
    }
  }

  async requestPasswordReset(email: string): Promise<void> {
    try {
      console.log('[SimpleAuthAPI] Requesting password reset for:', email);
      
      await this.makeRequest('/api/auth/request-password-reset', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      console.log('[SimpleAuthAPI] Password reset requested');
    } catch (error: any) {
      console.error('[SimpleAuthAPI] Password reset request failed:', error);
      throw error;
    }
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      console.log('[SimpleAuthAPI] Resetting password with token');
      
      const response = await this.makeRequest('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({
          reset_token: token,
          new_password: newPassword
        }),
      });

      console.log('[SimpleAuthAPI] Password reset successful');
    } catch (error: any) {
      console.error('[SimpleAuthAPI] Password reset failed:', error);
      throw error;
    }
  }

  async healthCheck(): Promise<boolean> {
    try {
      console.log('[SimpleAuthAPI] Checking auth service health');
      
      const response = await this.makeRequest('/api/auth/health', {
        method: 'GET'
      });

      return response.success;
    } catch (error: any) {
      console.error('[SimpleAuthAPI] Health check failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export const simpleAuthAPI = new SimpleAuthAPI();
export default simpleAuthAPI;